import { useMemo, useCallback, useState, useEffect } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Box, Checkbox, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useMainContext } from "../../../context/useMainContext";
import parseCheckData from "../../../utils/parseCheckData";
import { green, yellow, blue, indigo } from "@mui/material/colors";
import LazyCell from "./FieldTypes/LazyCell";
import HistoryCell from "./FieldTypes/HistoryCell";
import { getColumnWidth } from "../../../utils/UI/getColumnWidth";
import { updateUrlParams } from "../../../utils/urlParams";
import CustomPagination from "./CustomPagination";
import { getCellColor, getTableHeaders } from "../hooks/useTableColumnsConfig";
import { supabase } from "../../../supabase";

export default function DataGridTable() {
  const {
    rows,
    selected,
    setSelected,
    selectAll,
    setSelectAll,
    isGuest,
    sortConfig,
    setSortConfig,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    rowsCount,
    loadingData,
    setLoadingData,
    visibleColumns,
  } = useMainContext();

  const [expandedRows, setExpandedRows] = useState({});
  const [isDataGridInitialized, setIsDataGridInitialized] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState({});
  const columnHeaders = getTableHeaders({ columns: visibleColumns });

  const isRecentlyAdded = useCallback((row) => {
    if (!row.created_at) return false;
    const createdDate = new Date(row.created_at);
    const now = new Date();
    const diffInHours = (now - createdDate) / (1000 * 60 * 60);
    return diffInHours < 24;
  }, []);

  const loadHistoryData = useCallback(async (influencerId) => {
    if (loadedHistory[influencerId]) return;

    setLoadingData(true);

    try {
      const [freeCheckResult, paidCheckResult, statusHistoryResult] = await Promise.all([
        supabase
          .from("free_check_history")
          .select("*")
          .eq("influencer_id", influencerId)
          .order("timestamp", { ascending: false }),
        supabase
          .from("paid_check_history")
          .select("*")
          .eq("influencer_id", influencerId)
          .order("timestamp", { ascending: false }),
        supabase
          .from("status_history")
          .select("*")
          .eq("influencer_id", influencerId)
          .order("created_at", { ascending: false }),
      ]);

      setLoadedHistory((prev) => ({
        ...prev,
        [influencerId]: {
          free_check_history: freeCheckResult.data || [],
          paid_check_history: paidCheckResult.data || [],
          status_history: statusHistoryResult.data || [],
        },
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData(false);
    }
  }, [loadedHistory]);

  const handleToggleExpanded = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));

    if (!expandedRows[rowId]) {
      loadHistoryData(rowId);
    }
  }, [expandedRows, loadHistoryData]);

  const createExpandedRows = useCallback(
    (originalRows) => {
      const expandedRowsData = [];

      originalRows.forEach((row, index) => {
        const loadedHistoryData = loadedHistory[row.id] || {
          free_check_history: [],
          paid_check_history: [],
          status_history: [],
        };

        // Use data from row if available (updated by StatusCell), otherwise use loaded history
        const rowWithHistory = {
          ...row,
          free_check_history: row.free_check_history?.length > 0 ? row.free_check_history : loadedHistoryData.free_check_history,
          paid_check_history: row.paid_check_history?.length > 0 ? row.paid_check_history : loadedHistoryData.paid_check_history,
          status_history: row.status_history?.length > 0 ? row.status_history : loadedHistoryData.status_history,
        };

        // Add main row
        expandedRowsData.push({
          ...rowWithHistory,
          id: row.id,
          isMainRow: true,
          originalIndex: index,
        });

        // If row is expanded, add detail rows from history
        if (expandedRows[row.id]) {
          // Add latest free_check_history data
          const latestFreeCheck = rowWithHistory.free_check_history?.[0];
          if (latestFreeCheck) {
            const freeCheckData = parseCheckData(latestFreeCheck.raw_data);
            expandedRowsData.push({
              id: `${row.id}-free-check`,
              link: row.link,
              isDetailRow: true,
              detailType: "free-check",
              originalRow: rowWithHistory,
              timestamp: latestFreeCheck.timestamp,
              checked_by: latestFreeCheck.checked_by,
              ...freeCheckData,
              checkbox: null,
              status: null,
              notes: null,
              refusal_reason: null,
              firstname: null,
              nail_situation: null,
              _isFreeCheckData: true,
            });
          }

          // Add latest paid_check_history data
          const latestPaidCheck = rowWithHistory.paid_check_history?.[0];
          if (latestPaidCheck) {
            const paidCheckData = parseCheckData(latestPaidCheck.raw_data);
            expandedRowsData.push({
              id: `${row.id}-paid-check`,
              link: row.link,
              isDetailRow: true,
              detailType: "paid-check",
              originalRow: rowWithHistory,
              timestamp: latestPaidCheck.timestamp,
              checked_by: latestPaidCheck.checked_by,
              ...paidCheckData,
              checkbox: null,
              status: null,
              notes: null,
              refusal_reason: null,
              firstname: null,
              nail_situation: null,
              _isPaidCheckData: true,
            });
          }

          // Add status history info row - show if there's any history
          const hasAnyHistory =
            rowWithHistory.status_history?.length > 0 ||
            rowWithHistory.free_check_history?.length > 0 ||
            rowWithHistory.paid_check_history?.length > 0;

          if (hasAnyHistory) {
            expandedRowsData.push({
              id: `${row.id}-status-info`,
              link: row.link,
              isDetailRow: true,
              detailType: "status-info",
              originalRow: rowWithHistory,
              status_history: rowWithHistory.status_history,
              free_check_history: rowWithHistory.free_check_history,
              paid_check_history: rowWithHistory.paid_check_history,
              checkbox: null,
              status: null,
              notes: null,
              refusal_reason: null,
              firstname: null,
              nail_situation: null,
              _isStatusInfoData: true,
            });
          }
        }
      });
      return expandedRowsData;
    },
    [expandedRows, visibleColumns, loadedHistory]
  );

  const isArrayField = (fieldName, sampleData) => {
    return sampleData.some((row) => {
      const value = row[fieldName];
      return (
        Array.isArray(value) ||
        (typeof value === "string" &&
          (value.startsWith("[") || value.includes('"value"'))) ||
        (value && typeof value === "object" && !value.id && !value.timestamp)
      );
    });
  };

  const columnsDataGrid = useMemo(() => {
    const cols = [];
    // Add first checkbox column
    cols.push({
      field: "checkbox",
      headerName: "",
      width: getColumnWidth("checkbox"),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      pinned: "left",
      renderHeader: () => (
        <Checkbox
          checked={selectAll}
          onChange={(e) => {
            if (isGuest) return;
            const isChecked = e.target.checked;
            const currentPageIds = rows.map((r) => r.id);
            setSelectAll(isChecked);
            if (isChecked) {
              // Add all current page rows to selected, avoiding duplicates
              const newSelected = [
                ...selected.filter((item) => !currentPageIds.includes(item.id)),
                ...rows,
              ];
              setSelected(newSelected);
            } else {
              // Remove all current page rows from selected
              const newSelected = selected.filter(
                (item) => !currentPageIds.includes(item.id)
              );
              setSelected(newSelected);
            }
          }}
          disabled={isGuest}
          size="small"
        />
      ),
      renderCell: (params) => {
        const row = params.row;
        if (row.isDetailRow) {
          if (row.detailType === "status-info") {
            return <HistoryCell row={row} />;
          }
          return null;
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Checkbox
              checked={selected.some((item) => item.id === row.id)}
              onChange={() => {
                if (isGuest) return;
                setSelected((prevSelected) => {
                  const existingIndex = prevSelected.findIndex(
                    (item) => item.id === row.id
                  );
                  if (existingIndex !== -1) {
                    return prevSelected.filter(
                      (_, index) => index !== existingIndex
                    );
                  } else {
                    return [...prevSelected, row];
                  }
                });
              }}
              disabled={isGuest}
              size="small"
            />
            <IconButton
              size="small"
              sx={{ mt: -0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpanded(row.id);
              }}
            >
              {expandedRows[row.id] ? (
                <ExpandLessIcon sx={{ fontSize: "16px" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "16px" }} />
              )}
            </IconButton>
          </Box>
        );
      },
    });
    // Add other columns
    columnHeaders
      ?.filter((col) => col.key !== "actions")
      ?.forEach((col) => {
        const fieldIsArray = isArrayField(col.key, rows || []);
        cols.push({
          field: col.key,
          headerName: col.label,
          width: getColumnWidth(col.key),
          sortable: !fieldIsArray,
          filterable: true,
          pinned: ["status", "username"].includes(col.key)
            ? "left"
            : undefined,
          renderHeader: () => (
            <>
              {col.label}
              {!fieldIsArray && <UnfoldMoreIcon sx={{ fontSize: "16px" }} />}
            </>
          ),
          renderCell: (params) => {
            const row = params.row;
            return (
              <LazyCell
                col={col.key}
                row={row}
              />
            );
          },
        });
      });
    return cols;
  }, [
    columnHeaders,
    selected,
    setSelected,
    selectAll,
    setSelectAll,
    isGuest,
    rows,
    expandedRows,
    handleToggleExpanded,
  ]);

  const handleSortModelChange = useCallback(
    (model) => {
      if (!isDataGridInitialized) {
        return;
      }
      let newSortConfig;
      if (model.length > 0) {
        const sortItem = model[0];
        newSortConfig = {
          key: sortItem.field,
          direction: sortItem.sort,
        };
      } else {
        newSortConfig = { key: "username", direction: "asc" };
      }
      // Only update if the sort config actually changed
      if (
        !sortConfig ||
        sortConfig.key !== newSortConfig.key ||
        sortConfig.direction !== newSortConfig.direction
      ) {
        setSortConfig(newSortConfig);
        updateUrlParams({ sortConfig: newSortConfig });
      }
    },
    [setSortConfig, isDataGridInitialized, sortConfig]
  );

  const handlePaginationModelChange = useCallback(
    (newModel) => {
      const { page: newPage, pageSize: newPageSize } = newModel;
      setPage(newPage);
      setRowsPerPage(newPageSize);
      updateUrlParams({ page: newPage, rowsPerPage: newPageSize });
    },
    [setPage, setRowsPerPage]
  );

  const expandedRowsData = useMemo(() => {
    return createExpandedRows(rows || []);
  }, [rows, createExpandedRows]);

  const columnColors = useMemo(() => {
    const styles = {};
    styles[`& .MuiDataGrid-columnHeader[data-field="checkbox"]`] = {
      borderTop: `8px solid ${indigo['A100']}`,
    };
    columnHeaders?.forEach((col) => {
      const color = getCellColor({ columnKey: col.key });
      styles[`& .MuiDataGrid-columnHeader[data-field="${col.key}"]`] = {
        borderTop: `8px solid ${color}`,
      };
    });
    return styles;
  }, [columnHeaders, getCellColor]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDataGridInitialized(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%',
      flex: 1, 
      minHeight: 0,
      display: 'flex',
    }}>
      <DataGridPro
        rows={expandedRowsData}
        columns={columnsDataGrid}
        loading={loadingData}
        pagination
        paginationMode="server"
        rowCount={rowsCount}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[20, 100]}
        onSortModelChange={handleSortModelChange}
        sortModel={
          sortConfig && sortConfig.direction && isDataGridInitialized
            ? [{ field: sortConfig.key, sort: sortConfig.direction }]
            : []
        }
        pinnedColumns={{ left: ["checkbox", "status", "username"] }}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnMenu={false}
        disableColumnPinning={true}
        sortingMode="server"
        disableMultipleColumnsSorting={true}
        slots={{
          pagination: CustomPagination,
        }}
        getRowClassName={(params) => {
          if (params.row.isDetailRow) {
            if (params.row.detailType === "free-check") return "free-check-row";
            if (params.row.detailType === "paid-check") return "paid-check-row";
            if (params.row.detailType === "status-info") return "status-info-row";
          }
          if (isRecentlyAdded(params.row)) {
            return "recently-added-row";
          }
          return "";
        }}
        sx={{
          height: '100%',
          "& .MuiDataGrid-iconButtonContainer": {
            display: "none !important",
          },
          "& .MuiDataGrid-sortIcon": {
            display: "none !important",
          },
          "& .MuiDataGrid-columnHeader": {
            "&:hover .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
          },
          ...columnColors,
          "& .MuiDataGrid-row.free-check-row .MuiDataGrid-cell": {
            backgroundColor: `${green[50]}`,
          },
          "& .MuiDataGrid-row:hover.free-check-row .MuiDataGrid-cell": {
            backgroundColor: `${green[50]}`,
          },
          "& .MuiDataGrid-row.paid-check-row .MuiDataGrid-cell": {
            backgroundColor: `${yellow[50]}`,
          },
          "& .MuiDataGrid-row:hover.paid-check-row .MuiDataGrid-cell": {
            backgroundColor: `${yellow[50]}`,
          },
          "& .MuiDataGrid-row.status-info-row .MuiDataGrid-cell": {
            width: "max-content",
            left: "0px !important",
            borderRight: "none",
            borderBottom: "none",
          },
          "& .MuiDataGrid-row.status-info-row:hover": {
            backgroundColor: "transparent !important",
          },
          "& .MuiDataGrid-row.status-info-row:hover .MuiDataGrid-cell": {
            backgroundColor: "transparent !important",
          },
          "& .MuiDataGrid-row.status-info-row .MuiDataGrid-cell--withRightBorder":
            {
              display: "none",
            },
          // History cell
          "& .MuiDataGrid-row.status-info-row .MuiDataGrid-cell[data-field='checkbox']": {
            display: "flex",
          },
          "& .MuiDataGrid-row.status-info-row .MuiDataGrid-cell:not([data-field='checkbox'])": {
            display: "none",
          },
          // Styles for recently added rows (within 24 hours)
          "& .MuiDataGrid-row.recently-added-row .MuiDataGrid-cell": {
            backgroundColor: blue[50],
          },
          "& .MuiDataGrid-row:hover.recently-added-row .MuiDataGrid-cell": {
            backgroundColor: blue[50],
          },
        }}
      />
    </Box>
  );
}
